import axios from 'axios';
import {
  documentDirectory,
  getInfoAsync,
  readAsStringAsync,
  writeAsStringAsync,
  EncodingType,
} from 'expo-file-system';
import { Platform } from 'react-native';
import moment from 'moment';
import constants from '../config/constants';

const api = axios.create({
  baseURL: constants.address,
  timeout: 10000,
});

const LOG_FILE = `${documentDirectory}network_log.log`;

const ALLOWED_HEADERS = [
  'host',
  'user-agent',
  'accept',
  'accept-language',
  'accept-encoding',
  'referer',
  'connection',
  'content-type',
  'content-length',
  'date',
  'x-forwarded-for',
  'via',
  'x-real-ip',
  'x-forwarded-proto',
  'x-forwarded-host',
  'x-forwarded-port',
];

const filterHeaders = (headers: Record<string, string>) => {
  return Object.keys(headers).reduce(
    (filtered, key) => {
      if (ALLOWED_HEADERS.includes(key.toLowerCase())) {
        filtered[key] = headers[key];
      }
      return filtered;
    },
    {} as Record<string, string>,
  );
};

// Function to write logs, overwriting each day
const logRequest = async (logEntry: string) => {
  if (Platform.OS !== 'web') {
    try {
      const now = moment();
      const todayDate = now.format('YYYY-MM-DD');

      // Read the existing log to check if it’s from today
      const fileExists = await getInfoAsync(LOG_FILE);
      let currentLog = '';

      if (fileExists.exists) {
        currentLog = await readAsStringAsync(LOG_FILE);
        const firstLine = currentLog.split('\n')[0];

        // If the first log entry isn't from today, reset the file
        if (!currentLog || !firstLine.includes(todayDate)) {
          currentLog = ''; // Reset log file for new day
        }
      }

      // Add new log entry
      const updatedLog = `${currentLog}${logEntry}`;
      await writeAsStringAsync(LOG_FILE, updatedLog, {
        encoding: EncodingType.UTF8,
      });
    } catch (error) {
      // failed to write log
      return Promise.reject(error);
    }
  }
};

// Request interceptor
api.interceptors.request.use((request) => {
  const logEntry = `[${moment().format('YYYY-MM-DD HH:mm:ss')}] Request: ${request.method?.toUpperCase()} ${request.baseURL}${request.url} - Headers: ${JSON.stringify(filterHeaders(request.headers || {}))}\n`;
  logRequest(logEntry);
  return request;
});

// Response interceptor
api.interceptors.response.use(
  (response) => {
    const logEntry = `[${moment().format('YYYY-MM-DD HH:mm:ss')}] Response: ${response.status} ${response.config.url} - Headers: ${JSON.stringify(filterHeaders(response.headers || {}))}\n`;
    logRequest(logEntry);
    return response;
  },
  (error) => {
    const logEntry = `[${moment().format('YYYY-MM-DD HH:mm:ss')}] Error: ${error.response?.status || 'Unknown'} ${error.config?.url} - Message: ${error.message}\n`;
    logRequest(logEntry);
    return Promise.reject(error);
  },
);

export default api;
